'use client'

import { getUser } from '@/features/Auth/auth.hooks'
import React, { useEffect, useRef, useState } from 'react'
import { useZendesk } from 'react-use-zendesk'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createUserProfileQueryOptions } from '@/features/User/queries'
import { jwtDecode } from 'jwt-decode'
import * as Sentry from '@sentry/nextjs'

function waitForZendesk(timeoutMs = 10000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window !== 'undefined' && window.zE) return resolve()
    const start = Date.now()
    const interval = setInterval(() => {
      if (typeof window !== 'undefined' && window.zE) {
        clearInterval(interval)
        resolve()
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(interval)
        reject(new Error('Zendesk widget did not load in time'))
      }
    }, 100)
  })
}

export function DataProvider({ children }: {
  children: React.ReactNode;
}) {
  const { loginUser } = useZendesk()
  const tokenRef = useRef<string | null>(null)
  const queryClient = useQueryClient()

  const us = getUser()
  useQuery(createUserProfileQueryOptions(!!us))

  const getTokenString = () => {
    if (typeof window === 'undefined') return null
    try {
      const user = getUser()
      return user?.accessToken || null
    } catch {
      return null
    }
  }

  const [accessToken, setAccessToken] = useState<string | null>(() => {
    const token = getTokenString()
    tokenRef.current = token
    return token
  })

  useEffect(() => {
    const checkAndUpdateToken = () => {
      const token = getTokenString()
      if (token !== tokenRef.current) {
        tokenRef.current = token
        setAccessToken(token)
      }
    }

    const handleUserUpdate = () => {
      checkAndUpdateToken()
      queryClient.invalidateQueries()
    }

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'godefi:user') {
        checkAndUpdateToken()
      }
    }

    window.addEventListener('user:updated', handleUserUpdate)
    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('user:updated', handleUserUpdate)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [queryClient])

  // Keeps Sentry's user context in sync with the current session's JWT (`sub` = userId),
  // so captured errors carry user_id. Fires on fresh login and on an existing session
  // restored at page load, since accessToken is already tracked for both. Clearing on
  // logout is handled separately in useLogout, since same-tab localStorage removal here
  // wouldn't otherwise trigger this effect.
  useEffect(() => {
    if (!accessToken) return
    try {
      const { sub } = jwtDecode<{ sub?: string }>(accessToken)
      if (sub) Sentry.setUser({ id: sub })
    } catch (error) {
      console.error('Failed to decode accessToken for Sentry user context:', error)
    }
  }, [accessToken])

  const prevZendeskTokenRef = useRef<string | null>(null)
  const zendeskLoginInFlightRef = useRef(false)

  useEffect(() => {
    if (accessToken && accessToken !== prevZendeskTokenRef.current && !zendeskLoginInFlightRef.current) {
      prevZendeskTokenRef.current = accessToken
      zendeskLoginInFlightRef.current = true

      const init = async () => {
        try {
          await waitForZendesk()
          const data = await fetch('/api/jwt', { headers: { Authorization: `Bearer ${accessToken}` } })
          const innerToken = await data.json()
          loginUser(innerToken.token, (error) => {
            if (error) console.error('Zendesk auth failed:', error)
          })
        } catch (error) {
          console.error('--error', error)
        } finally {
          zendeskLoginInFlightRef.current = false
        }
      }

      init()
    }
  }, [accessToken])

  return children
}
